import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/shared/Layout';
import { generateReport } from '../utils/api';
import { calcDerivedValues } from '../utils/parScore';
import { useAuth } from '../context/AuthContext';
import './NewReportPage.css';

const EMPTY = { actives: '', pendings: '', reductions: '', solds: '', faileds: '', address: '' };

export default function NewReportPage() {
  const { agent, refreshAgent } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const num = (v) => v === '' ? '' : parseInt(v) || 0;

  // Live PAR score — only calc when all 5 numbers are filled
  const allFilled = ['actives','pendings','reductions','solds','faileds'].every(k => form[k] !== '');
  const derived = allFilled ? calcDerivedValues(
    num(form.actives), num(form.pendings), num(form.reductions), num(form.solds), num(form.faileds)
  ) : null;

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (agent?.credits < 1) return setError('You need at least 1 credit to generate a report.');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('property_address', form.address);
      fd.append('active_count',    num(form.actives));
      fd.append('pending_count',   num(form.pendings));
      fd.append('price_reductions',num(form.reductions));
      fd.append('sold_count',      num(form.solds));
      fd.append('failed_count',    num(form.faileds));
      fd.append('par_score',       derived?.score ?? 0);
      if (photo) fd.append('property_photo', photo);

      const res = await generateReport(fd);
      await refreshAgent();
      navigate(`/reports/${res.data.report_id}/done`, { state: { pdf_url: res.data.pdf_url, address: form.address, score: derived?.score, scoreLabel: derived?.scoreLabel, scoreColor: derived?.scoreColor }});
    } catch (err) {
      setError(err.response?.data?.error || 'Could not generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="new-report-layout">

        {/* ── Left: Form ── */}
        <div className="report-form-col">
          <div className="card">
            <h1 className="card-title" style={{fontSize:20, marginBottom:4}}>New PAR Report</h1>
            <p className="card-subtitle" style={{marginBottom:24}}>Enter the market data from your MLS search</p>

            {agent?.credits < 1 && (
              <div className="alert alert-error">
                You have no credits. <Link to="/credits"><strong>Buy credits</strong></Link> to generate a report.
              </div>
            )}
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>

              {/* Property address */}
              <div className="form-group">
                <label className="form-label">Property Address *</label>
                <input className="form-input" required value={form.address}
                  onChange={e => set('address', e.target.value)}
                  placeholder="1413 SE 36th Ter, Cape Coral, FL 33904" />
              </div>

              {/* Market numbers */}
              <div className="section-divider">Market Data</div>
              <p className="section-note">Count these from your MLS search for the subject property's area.</p>

              <div className="numbers-grid">
                <div className="form-group">
                  <label className="form-label">Active Listings</label>
                  <input className="form-input number-input" type="number" min="0" required
                    value={form.actives} onChange={e => set('actives', e.target.value)} placeholder="0" />
                  <span className="form-hint">Currently for sale</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Pending</label>
                  <input className="form-input number-input" type="number" min="0" required
                    value={form.pendings} onChange={e => set('pendings', e.target.value)} placeholder="0" />
                  <span className="form-hint">Under contract</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Price Reductions</label>
                  <input className="form-input number-input" type="number" min="0" required
                    value={form.reductions} onChange={e => set('reductions', e.target.value)} placeholder="0" />
                  <span className="form-hint">Actives with a cut</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Sold / Closed</label>
                  <input className="form-input number-input" type="number" min="0" required
                    value={form.solds} onChange={e => set('solds', e.target.value)} placeholder="0" />
                  <span className="form-hint">Recently closed</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Did Not Sell</label>
                  <input className="form-input number-input" type="number" min="0" required
                    value={form.faileds} onChange={e => set('faileds', e.target.value)} placeholder="0" />
                  <span className="form-hint">Expired / withdrawn</span>
                </div>
              </div>

              {/* Optional photo */}
              <div className="section-divider">Property Photo <span className="optional-tag">Optional</span></div>
              <div className="photo-upload-area" onClick={() => fileRef.current.click()}>
                {photoPreview
                  ? <img src={photoPreview} alt="property" className="photo-preview"/>
                  : <div className="photo-placeholder">
                      <span className="photo-icon">🏠</span>
                      <p>Click to upload a photo of the property</p>
                      <p className="form-hint">JPG or PNG, max 5MB</p>
                    </div>
                }
              </div>
              <input type="file" ref={fileRef} accept="image/*" style={{display:'none'}} onChange={handlePhoto}/>
              {photo && (
                <button type="button" className="btn btn-ghost btn-sm" style={{marginTop:8}}
                  onClick={() => { setPhoto(null); setPhotoPreview(null); }}>
                  Remove photo
                </button>
              )}

              <button
                className="btn btn-primary btn-full btn-lg"
                style={{marginTop: 28}}
                type="submit"
                disabled={loading || agent?.credits < 1}
              >
                {loading
                  ? <><div className="spinner"/>Generating report…</>
                  : `Generate Report — 1 Credit`
                }
              </button>

              <p style={{textAlign:'center', fontSize:13, color:'var(--gray-400)', marginTop:10}}>
                You have <strong style={{color: agent?.credits > 0 ? 'var(--navy)' : 'var(--red)'}}>
                  {agent?.credits ?? 0} credit{agent?.credits !== 1 ? 's' : ''}
                </strong> remaining
              </p>
            </form>
          </div>
        </div>

        {/* ── Right: Live PAR Score Preview ── */}
        <div className="score-preview-col">
          <div className="card score-preview-card">
            <h3 style={{fontSize:14, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:20}}>
              Live PAR Score Preview
            </h3>

            {!derived ? (
              <div className="score-empty">
                <p>Fill in the market numbers to see the PAR score preview.</p>
              </div>
            ) : (
              <>
                {/* Big score */}
                <div className="big-score" style={{borderColor: derived.scoreColor}}>
                  <div className="big-score-number" style={{color: derived.scoreColor}}>
                    {derived.score}
                  </div>
                  <div className="big-score-label" style={{color: derived.scoreColor}}>
                    {derived.scoreLabel}
                  </div>
                  <div className="big-score-growth">{derived.scoreGrowth} annual growth</div>
                </div>

                {/* Three metrics */}
                <div className="metrics-list">
                  <div className="metric-row">
                    <span className="metric-name">Pending to Active</span>
                    <span className="metric-val">{derived.metric1}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-name">Price Corrections</span>
                    <span className="metric-val">{derived.metric2}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-name">Leaving Market</span>
                    <span className="metric-val">{derived.metric3}</span>
                  </div>
                </div>

                {/* Derived values */}
                <div className="derived-grid">
                  <div className="derived-item"><span className="derived-val">{derived.pm}</span><span className="derived-lbl">Previous Market</span></div>
                  <div className="derived-item"><span className="derived-val">{derived.om}</span><span className="derived-lbl">On Market</span></div>
                  <div className="derived-item"><span className="derived-val">{derived.fp}</span><span className="derived-lbl">Failure Rate</span></div>
                  <div className="derived-item"><span className="derived-val">{derived.ft}</span><span className="derived-lbl">Failed Target</span></div>
                  <div className="derived-item"><span className="derived-val">{derived.prt}</span><span className="derived-lbl">Reduction Target</span></div>
                  <div className="derived-item"><span className="derived-val">{derived.at}</span><span className="derived-lbl">Active Market</span></div>
                </div>
              </>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}
